import type { Item } from '@erp/shared';
import { ChevronDown, Loader2, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useLayoutDirection } from '../../hooks/useDirection.js';
import { api } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';

export interface ItemPickerProps {
  id?: string;
  /** Selected product id, or null. */
  value: string | null;
  /** Label to show for an already-selected product (the API hands back `targetName`). */
  valueLabel: string | null;
  onChange: (itemId: string | null, label: string | null) => void;
  placeholder?: string;
  ariaLabel?: string;
  emptyLabel: string;
}

interface Rect { left: number; top: number; width: number }

/**
 * A product picker that searches the SERVER as you type.
 *
 * Deliberately not the shared Combobox: that one filters the option list it is
 * handed, and `GET /items` caps at 500 rows — on a catalogue of thousands most
 * products would simply be unpickable, so a discount could never be pointed at
 * them. Everything else (look, keyboard nav, portaled dropdown) mirrors Combobox
 * on purpose; the portal is what keeps the menu above the sibling glass cards,
 * which each open their own stacking context.
 */
export function ItemPicker({
  id,
  value,
  valueLabel,
  onChange,
  placeholder,
  ariaLabel,
  emptyLabel,
}: ItemPickerProps): JSX.Element {
  const { i18n } = useTranslation();
  const dir = useLayoutDirection();
  const locale = i18n.language.split('-')[0] ?? 'en';
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const h = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(h);
  }, [query]);

  // Only queries while open, so a form that never touches this field costs
  // nothing. An empty query still fetches: an operator who clicks the field
  // expecting a list of products should get one.
  useEffect(() => {
    if (!open) return undefined;
    let on = true;
    setLoading(true);
    const params: Record<string, string | number> = { limit: 25, locale };
    if (debounced) params.q = debounced;
    void api()
      .get<{ items: Item[] }>('/items', { params })
      .then((r) => {
        if (!on) return;
        setResults(r.data.items.filter((it) => it.isActive));
        setActive(0);
      })
      .catch(() => { if (on) setResults([]); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [open, debounced, locale]);

  // Close on outside click (the menu is portaled, so it lives outside root).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Keep the portaled menu glued to the input while scrolling / resizing.
  useEffect(() => {
    if (!open) { setRect(null); return undefined; }
    const place = (): void => {
      const r = rootRef.current?.getBoundingClientRect();
      if (r) setRect({ left: r.left, top: r.bottom + 4, width: r.width });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const label = (it: Item): string => `${it.code} — ${it.name}`;

  const choose = (it: Item): void => {
    onChange(it.id, label(it));
    setOpen(false);
    setQuery('');
    setActive(0);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      // Swallowed even with no match, or the form would submit mid-search.
      e.preventDefault();
      const it = results[Math.min(active, results.length - 1)];
      if (open && it) choose(it);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  const display = open ? query : valueLabel ?? '';

  return (
    <div ref={rootRef} className="relative">
      <Search className="pointer-events-none absolute inset-y-0 start-2.5 my-auto h-4 w-4 text-fg-muted" aria-hidden />
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-label={ariaLabel}
        autoComplete="off"
        className={cn(
          'block h-10 w-full rounded-md border border-border bg-bg ps-8 pe-8 text-sm text-fg outline-none transition',
          'placeholder:text-fg-muted focus:border-brand focus:ring-2 focus:ring-brand/20',
        )}
        placeholder={placeholder}
        value={display}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onFocus={(e) => { setOpen(true); setQuery(''); e.target.select(); }}
        onKeyDown={onKeyDown}
      />
      {value ? (
        <button
          type="button"
          aria-label="Clear"
          onClick={() => { onChange(null, null); setQuery(''); setOpen(false); }}
          className="absolute inset-y-0 end-2 my-auto grid h-5 w-5 place-items-center rounded text-fg-muted hover:text-fg"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : loading ? (
        <Loader2 className="pointer-events-none absolute inset-y-0 end-2.5 my-auto h-4 w-4 animate-spin text-fg-muted" aria-hidden />
      ) : (
        <ChevronDown className="pointer-events-none absolute inset-y-0 end-2.5 my-auto h-4 w-4 text-fg-muted" aria-hidden />
      )}
      {open && rect && createPortal(
        <ul
          ref={menuRef}
          dir={dir}
          className="glass-overlay fixed z-[60] max-h-64 overflow-auto rounded-md border py-1 shadow-lg"
          style={{ left: rect.left, top: rect.top, width: rect.width }}
          role="listbox"
        >
          {results.length === 0 ? (
            <li className="px-3 py-2 text-sm text-fg-muted">{emptyLabel}</li>
          ) : (
            results.map((it, i) => (
              <li key={it.id} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); choose(it); }}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    'flex w-full flex-col items-start px-3 py-1.5 text-start text-sm',
                    i === active ? 'bg-bg-subtle' : 'hover:bg-bg-subtle',
                  )}
                >
                  <span className="font-medium text-fg">{label(it)}</span>
                  {it.barcode && <span className="font-mono text-xs text-fg-muted">{it.barcode}</span>}
                </button>
              </li>
            ))
          )}
        </ul>,
        document.body,
      )}
    </div>
  );
}
